import { useEffect, useRef, useState } from 'react';
import { Building2, Check, ChevronDown, Layers } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface ProjectOption {
  id: string;
  name: string;
  /** #RRGGBB or null — see lib/accent.ts. */
  color: string | null;
  status: string;
  location: string;
}

/**
 * Current-project selector at the head of the sidebar, on every screen.
 *
 * Scopes the whole app — see CurrentProjectContext. The first choice is All
 * projects (an empty id), which every screen reads as "no filter". Collapses
 * to a single icon button on the rail that opens the same list.
 */
export function ProjectSelector({
  projects,
  currentId,
  onChange,
  collapsed = false,
}: {
  projects: ProjectOption[];
  currentId: string;
  onChange: (id: string) => void;
  collapsed?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const current = projects.find((project) => project.id === currentId);
  const allLabel = `${projects.length} active ${projects.length === 1 ? 'project' : 'projects'}`;

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);


  return (
    <div ref={containerRef} className="relative">
      {collapsed ? (
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          title={current ? `${current.name} — ${current.location}` : 'All projects'}
          aria-label={`Current project: ${current?.name ?? 'All projects'}`}
          aria-expanded={open}
          className={cn(
            'mx-auto flex h-10 w-10 items-center justify-center rounded-lg transition-colors',
            current ? 'bg-accent text-accent-on' : 'bg-white/10 text-white hover:bg-white/[0.18]',
          )}
        >
          {current ? <Building2 aria-hidden className="h-[18px] w-[18px]" /> : <Layers aria-hidden className="h-[18px] w-[18px]" />}
        </button>
      ) : (
      <>
      <p className="px-1 pb-2 text-2xs font-semibold uppercase tracking-[0.1em] text-white/45">
        Current Project
      </p>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="flex w-full items-center gap-3 rounded-lg bg-white/[0.07] px-3 py-2.5 text-left transition-colors hover:bg-white/[0.12]"
      >
        <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded transition-colors', current ? 'bg-accent text-accent-on' : 'bg-white/10 text-white')}>
          {current ? <Building2 aria-hidden className="h-4 w-4" /> : <Layers aria-hidden className="h-4 w-4" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-white">{current?.name ?? 'All projects'}</span>
          <span className="mt-0.5 flex items-center gap-1.5 text-2xs text-white/55">
            <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent-400" />
            <span className="truncate">{current ? `${current.status} · ${current.location}` : allLabel}</span>
          </span>
        </span>
        <ChevronDown
          aria-hidden
          className={cn('h-4 w-4 shrink-0 text-white/55 transition-transform', open && 'rotate-180')}
        />
      </button>
      </>
      )}

      {open && (
        <ul
          role="listbox"
          className={cn(
            'absolute top-full z-40 mt-1.5 max-h-80 animate-fade-in overflow-y-auto rounded-lg border border-white/10 bg-midnight p-1.5 shadow-flyout scrollbar-slim-dark',
            collapsed ? 'left-0 w-64' : 'inset-x-0',
          )}
        >
          <li>
            <button
              type="button"
              role="option"
              aria-selected={!current}
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center gap-2.5 rounded px-2.5 py-2 text-left transition-colors',
                !current ? 'bg-white/[0.12] text-white' : 'text-white/75 hover:bg-white/[0.08] hover:text-white',
              )}
            >
              <Layers aria-hidden className="h-4 w-4 shrink-0 text-white/60" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">All projects</span>
                <span className="block truncate text-2xs text-white/50">Every site, combined</span>
              </span>
              {!current && <Check aria-hidden className="h-4 w-4 shrink-0 text-accent-400" />}
            </button>
          </li>
          <li aria-hidden className="my-1 border-t border-white/10" />
          {projects.map((project) => {
            const active = project.id === currentId;
            return (
              <li key={project.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    onChange(project.id);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded px-2.5 py-2 text-left transition-colors',
                    active ? 'bg-white/[0.12] text-white' : 'text-white/75 hover:bg-white/[0.08] hover:text-white',
                  )}
                >
                  {/* The project's own colour (user data), or the brand accent. */}
                  <span
                    aria-hidden
                    className={cn('h-3 w-3 shrink-0 rounded-full ring-1 ring-white/20', !project.color && 'bg-signal-400')}
                    style={project.color ? { backgroundColor: project.color } : undefined}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{project.name}</span>
                    <span className="block truncate text-2xs text-white/50">
                      {project.status} · {project.location}
                    </span>
                  </span>
                  {active && <Check aria-hidden className="h-4 w-4 shrink-0 text-accent-400" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
